import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../lib/analytics';

import { signOut } from '../../../redux/auth/auth.actions';

import { selectIsSidebarProfileMenuEnabled } from '../../../redux/main/main.selectors';
import { selectSidebarCollapsed } from '../../../redux/preferences/preferences.selectors';
import { toggleSidebarProfileMenu } from '../../../redux/main/main.actions';

import { toggleBtnModalShortcuts } from '../../../redux/diagram/diagram.actions';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';

import {
    ModeDarkIcon,
    ModeLightIcon,
    OkCheckIcon,
} from '../../Icon';

import './ModalProfileMenu.scss';
import { LANGUAGES } from '../../../i18n/menuLanguages';

export const ModalProfileMenu = () => {
    const history = useHistory();

    const isSidebarCollapsed = useSelector(selectSidebarCollapsed);
    const isOpen = useSelector(selectIsSidebarProfileMenuEnabled);

    const [shouldRender, setShouldRender] = useState(isOpen);
    const [animationClass, setAnimationClass] = useState('');

    const { map: shortKeysMap } = useShortKeysNavigation();
    const { t, i18n } = useTranslation();

    const openSubMenuTimerRef = useRef(null);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

    const cancelOpenSubMenuTimer = () => {
        if (openSubMenuTimerRef.current) {
            clearTimeout(openSubMenuTimerRef.current);
            openSubMenuTimerRef.current = null;
        }
    };

    useEffect(() => () => cancelOpenSubMenuTimer(), []);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setAnimationClass('fade-in');
        } else if (shouldRender) {
            setAnimationClass('fade-out');
        }
    }, [isOpen]);

    const handleAnimationEnd = () => {
        if (!isOpen) {
            setShouldRender(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const dispatch = useDispatch();

    const handleCancel = () => {
        dispatch(toggleSidebarProfileMenu({}));
        setIsSubMenuOpen(false);
    };

    // Hovering any menu item other than "Language" closes the sub-menu; hovering
    // "Language" opens it. Delegated on the container so every item (incl. disabled
    // ones) is covered without repeating handlers on each button.
    const handleMenuMouseOver = (e) => {
        const item = e.target.closest?.('.control-button');
        if (item && !item.classList.contains('language-trigger')) {
            cancelOpenSubMenuTimer();
            setIsSubMenuOpen(false);
        }
    };

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (!isOpen) return;

            if (event.code === 'Escape') {
                event.preventDefault();

                handleCancel();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [isOpen, handleCancel]);

    if (!shouldRender) return null;

    return (
        <div className="modal-profile-menu">
            <div className="modal-overlay" onClick={handleOverlayClick}></div>
            <div
                className={`profile-menu ${animationClass} ${isSidebarCollapsed ? 'collapsed' : ''}`}
                onAnimationEnd={handleAnimationEnd}
                onMouseOver={handleMenuMouseOver}
                onMouseLeave={() => {
                    // Only cancel a still-pending open — an already-open sub-menu stays
                    // open until another menu item is hovered or the user clicks outside.
                    cancelOpenSubMenuTimer();
                }}
            >
                <div className="control">
                    <button
                        className={`control-button`}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onClick={() => {
                            trackEvent('app_sidebar_profile_view_profile', {
                                page: 'app',
                                type: 'sidebar',
                                subtype: 'profile',
                            });

                            const url = `/settings/profile/`;
                            // setTimeout(() => {
                            dispatch(toggleSidebarProfileMenu());
                            history.push(url);
                            // }, 100);
                        }}
                    >
                        {/* <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M8.4 3H4.6C4.03995 3 3.75992 3 3.54601 3.10899C3.35785 3.20487 3.20487 3.35785 3.10899 3.54601C3 3.75992 3 4.03995 3 4.6V8.4C3 8.96005 3 9.24008 3.10899 9.45399C3.20487 9.64215 3.35785 9.79513 3.54601 9.89101C3.75992 10 4.03995 10 4.6 10H8.4C8.96005 10 9.24008 10 9.45399 9.89101C9.64215 9.79513 9.79513 9.64215 9.89101 9.45399C10 9.24008 10 8.96005 10 8.4V4.6C10 4.03995 10 3.75992 9.89101 3.54601C9.79513 3.35785 9.64215 3.20487 9.45399 3.10899C9.24008 3 8.96005 3 8.4 3Z"></path><path d="M19.4 3H15.6C15.0399 3 14.7599 3 14.546 3.10899C14.3578 3.20487 14.2049 3.35785 14.109 3.54601C14 3.75992 14 4.03995 14 4.6V8.4C14 8.96005 14 9.24008 14.109 9.45399C14.2049 9.64215 14.3578 9.79513 14.546 9.89101C14.7599 10 15.0399 10 15.6 10H19.4C19.9601 10 20.2401 10 20.454 9.89101C20.6422 9.79513 20.7951 9.64215 20.891 9.45399C21 9.24008 21 8.96005 21 8.4V4.6C21 4.03995 21 3.75992 20.891 3.54601C20.7951 3.35785 20.6422 3.20487 20.454 3.10899C20.2401 3 19.9601 3 19.4 3Z"></path><path d="M19.4 14H15.6C15.0399 14 14.7599 14 14.546 14.109C14.3578 14.2049 14.2049 14.3578 14.109 14.546C14 14.7599 14 15.0399 14 15.6V19.4C14 19.9601 14 20.2401 14.109 20.454C14.2049 20.6422 14.3578 20.7951 14.546 20.891C14.7599 21 15.0399 21 15.6 21H19.4C19.9601 21 20.2401 21 20.454 20.891C20.6422 20.7951 20.7951 20.6422 20.891 20.454C21 20.2401 21 19.9601 21 19.4V15.6C21 15.0399 21 14.7599 20.891 14.546C20.7951 14.3578 20.6422 14.2049 20.454 14.109C20.2401 14 19.9601 14 19.4 14Z"></path><path d="M8.4 14H4.6C4.03995 14 3.75992 14 3.54601 14.109C3.35785 14.2049 3.20487 14.3578 3.10899 14.546C3 14.7599 3 15.0399 3 15.6V19.4C3 19.9601 3 20.2401 3.10899 20.454C3.20487 20.6422 3.35785 20.7951 3.54601 20.891C3.75992 21 4.03995 21 4.6 21H8.4C8.96005 21 9.24008 21 9.45399 20.891C9.64215 20.7951 9.79513 20.6422 9.89101 20.454C10 20.2401 10 19.9601 10 19.4V15.6C10 15.0399 10 14.7599 9.89101 14.546C9.79513 14.3578 9.64215 14.2049 9.45399 14.109C9.24008 14 8.96005 14 8.4 14Z"></path></svg> */}
                        {/* <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V14M5 10V3M12 21V12M12 8V3M19 21V16M19 12V3M2 14H8M9 8H15M16 16H22"></path></svg> */}
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                        {t('m_profile_menu.profile')}
                    </button>
                </div>



                {/* <div className="control">
                    <button
                        // className={`control-button ${isBtnExportCodeEnabled ? 'selected' : ''}`}
                        className={`control-button control-button-with-key`}
                        type="button"
                        title=""
                        aria-label="Color"
                        aria-pressed="false"
                        onClick={() => {
                            // dispatch(toggleBtnExportCode());
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <ModeLightIcon />
                            <span>Toggle theme</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                            <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                            <span className="control-button-with-key-right-part-connector">+</span>
                            <span className="control-button-with-key-right-part-text">M</span>
                        </div>
                    </button>
                </div> */}

                {/* <div className="control">
                    <button
                        className={`control-button control-button-with-key disabled`}
                        type="button"
                    >
                        <div className='control-button-with-key-left-part'>
                            <ModeLightIcon />
                            <span>Toggle theme</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                        </div>
                    </button>
                </div> */}

                <div className="horizontal-line"></div>

                <div className="control">
                    <button
                        className={`control-button control-button-with-key language-trigger ${isSubMenuOpen ? 'selected' : ''}`}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onMouseEnter={() => {
                            if (isSubMenuOpen) return;
                            cancelOpenSubMenuTimer();
                            openSubMenuTimerRef.current = setTimeout(() => {
                                openSubMenuTimerRef.current = null;
                                trackEvent('app_sidebar_profile_language_menu', {
                                    page: 'app',
                                    type: 'sidebar',
                                    subtype: 'profile',
                                });
                                setIsSubMenuOpen(true);
                            }, 150);
                        }}
                        onMouseLeave={cancelOpenSubMenuTimer}
                        onClick={() => {
                            cancelOpenSubMenuTimer();
                            setIsSubMenuOpen(true);
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <svg className="icon-language" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1Zm8 11c0 .7-.1 1.4-.3 2-.6-1.5-1.6-3.1-3-4.7l1.8-1.8c1 1.3 1.5 2.8 1.5 4.5ZM6.5 6.5c1.3 0 3.6.8 6 2.9l-3.2 3.2C7.1 9.8 6.5 7.5 6.5 6.5Zm8.1 5c2.3 2.7 2.9 5 2.9 6-1.3 0-3.6-.8-6-2.9l3.1-3.1Zm1.9-6.1-1.9 1.9c-1.6-1.4-3.2-2.4-4.7-3 .7-.2 1.3-.3 2-.3 1.8 0 3.3.5 4.6 1.4ZM4 12c0-.7.1-1.4.3-2 .6 1.5 1.6 3.1 3 4.7l-1.8 1.8C4.5 15.2 4 13.7 4 12Zm3.5 6.6 1.9-1.9c1.6 1.4 3.2 2.4 4.7 3-.7.2-1.3.3-2 .3-1.8 0-3.3-.5-4.6-1.4Z" fill="currentColor" stroke="black" strokeWidth="0.8px"></path></svg>
                            {t('dropdown_ctrl_settings.language')}
                        </div>
                        <div className='control-button-with-key-right-part'>
                            <svg className="icon-arrow-lang" width="16px" height="18px" fill="none" viewBox="0 -2 24 24" stroke="#8e94afb3" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" stroke="rgb(134, 145, 148)"></path></svg>
                        </div>
                    </button>
                </div>

                <div className="control">
                    <button
                        className={`control-button control-button-with-key`}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onClick={() => {
                            trackEvent('app_sidebar_profile_preferences', {
                                page: 'app',
                                type: 'sidebar',
                                subtype: 'profile',
                            });

                            const url = `/settings/preferences/`;
                            dispatch(toggleSidebarProfileMenu());
                            history.push(url);
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <svg className="control-icon-preferences" width="14px" height="20px" viewBox="0 0 24 24" fill="none"><g> <path d="M2 20C2 19.4477 2.44772 19 3 19H10C10.5523 19 11 19.4477 11 20C11 20.5523 10.5523 21 10 21H3C2.44772 21 2 20.5523 2 20Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 19V17C15 16.4477 14.5523 16 14 16C13.4477 16 13 16.4477 13 17V23C13 23.5523 13.4477 24 14 24C14.5523 24 15 23.5523 15 23V21H21C21.5523 21 22 20.5523 22 20C22 19.4477 21.5523 19 21 19H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13L21 13C21.5523 13 22 12.5523 22 12C22 11.4477 21.5523 11 21 11L12 11Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 12C2 11.4477 2.44772 11 3 11H7V9C7 8.44772 7.44772 8 8 8C8.55228 8 9 8.44772 9 9V15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15V13H3C2.44772 13 2 12.5523 2 12Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 3V1C15 0.447716 14.5523 0 14 0C13.4477 0 13 0.447716 13 1V7C13 7.55228 13.4477 8 14 8C14.5523 8 15 7.55228 15 7V5H21C21.5523 5 22 4.55228 22 4C22 3.44772 21.5523 3 21 3H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 4C2 4.55228 2.44772 5 3 5H10C10.5523 5 11 4.55228 11 4C11 3.44772 10.5523 3 10 3H3C2.44772 3 2 3.44772 2 4Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> </g></svg>
                            <span>{t('dropdown_ctrl_settings.preferences')}</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                            {/* <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                            <span className="control-button-with-key-right-part-connector">+</span>
                            <span className="control-button-with-key-right-part-text">.</span> */}
                        </div>
                    </button>
                </div>

                {/* <div className="control">
                    <button
                        className={`control-button control-button-with-key disabled`}
                        type="button"
                        onClick={() => {
                            setTimeout(() => { dispatch(toggleBtnModalShortcuts()); }, 20);
                            setTimeout(() => { handleCancel(); }, 70);
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <svg className="control-icon-preferences" width="14px" height="20px" viewBox="0 0 24 24" fill="none"><g> <path d="M2 20C2 19.4477 2.44772 19 3 19H10C10.5523 19 11 19.4477 11 20C11 20.5523 10.5523 21 10 21H3C2.44772 21 2 20.5523 2 20Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 19V17C15 16.4477 14.5523 16 14 16C13.4477 16 13 16.4477 13 17V23C13 23.5523 13.4477 24 14 24C14.5523 24 15 23.5523 15 23V21H21C21.5523 21 22 20.5523 22 20C22 19.4477 21.5523 19 21 19H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13L21 13C21.5523 13 22 12.5523 22 12C22 11.4477 21.5523 11 21 11L12 11Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 12C2 11.4477 2.44772 11 3 11H7V9C7 8.44772 7.44772 8 8 8C8.55228 8 9 8.44772 9 9V15C9 15.5523 8.55228 16 8 16C7.44772 16 7 15.5523 7 15V13H3C2.44772 13 2 12.5523 2 12Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M15 3V1C15 0.447716 14.5523 0 14 0C13.4477 0 13 0.447716 13 1V7C13 7.55228 13.4477 8 14 8C14.5523 8 15 7.55228 15 7V5H21C21.5523 5 22 4.55228 22 4C22 3.44772 21.5523 3 21 3H15Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> <path d="M2 4C2 4.55228 2.44772 5 3 5H10C10.5523 5 11 4.55228 11 4C11 3.44772 10.5523 3 10 3H3C2.44772 3 2 3.44772 2 4Z" fill="currentColor" strokeWidth="0.5px" stroke="currentColor"></path> </g></svg>
                            <span>Preferences</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                        </div>
                    </button>
                </div> */}

                {/* <div className="control">
                    <button
                        className={`control-button control-button-with-key`}
                        type="button"
                        title=""
                        aria-label="Color"
                        aria-pressed="false"
                        onClick={() => {
                            setTimeout(() => { dispatch(toggleBtnModalShortcuts()); }, 20);
                            setTimeout(() => { handleCancel(); }, 70);
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <svg className="w-menu-icon-settings" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"></path><path d="M18.7273 14.7273C18.6063 15.0015 18.5702 15.3056 18.6236 15.6005C18.6771 15.8954 18.8177 16.1676 19.0273 16.3818L19.0818 16.4364C19.2509 16.6052 19.385 16.8057 19.4765 17.0265C19.568 17.2472 19.6151 17.4838 19.6151 17.7227C19.6151 17.9617 19.568 18.1983 19.4765 18.419C19.385 18.6397 19.2509 18.8402 19.0818 19.0091C18.913 19.1781 18.7124 19.3122 18.4917 19.4037C18.271 19.4952 18.0344 19.5423 17.7955 19.5423C17.5565 19.5423 17.3199 19.4952 17.0992 19.4037C16.8785 19.3122 16.678 19.1781 16.5091 19.0091L16.4545 18.9545C16.2403 18.745 15.9682 18.6044 15.6733 18.5509C15.3784 18.4974 15.0742 18.5335 14.8 18.6545C14.5311 18.7698 14.3018 18.9611 14.1403 19.205C13.9788 19.4489 13.8921 19.7347 13.8909 20.0273V20.1818C13.8909 20.664 13.6994 21.1265 13.3584 21.4675C13.0174 21.8084 12.5549 22 12.0727 22C11.5905 22 11.1281 21.8084 10.7871 21.4675C10.4461 21.1265 10.2545 20.664 10.2545 20.1818V20.1C10.2475 19.7991 10.1501 19.5073 9.97501 19.2625C9.79991 19.0176 9.55521 18.8312 9.27273 18.7273C8.99853 18.6063 8.69437 18.5702 8.39947 18.6236C8.10456 18.6771 7.83244 18.8177 7.61818 19.0273L7.56364 19.0818C7.39478 19.2509 7.19425 19.385 6.97353 19.4765C6.7528 19.568 6.51621 19.6151 6.27727 19.6151C6.03834 19.6151 5.80174 19.568 5.58102 19.4765C5.36029 19.385 5.15977 19.2509 4.99091 19.0818C4.82186 18.913 4.68775 18.7124 4.59626 18.4917C4.50476 18.271 4.45766 18.0344 4.45766 17.7955C4.45766 17.5565 4.50476 17.3199 4.59626 17.0992C4.68775 16.8785 4.82186 16.678 4.99091 16.5091L5.04545 16.4545C5.25503 16.2403 5.39562 15.9682 5.4491 15.6733C5.50257 15.3784 5.46647 15.0742 5.34545 14.8C5.23022 14.5311 5.03887 14.3018 4.79497 14.1403C4.55107 13.9788 4.26526 13.8921 3.97273 13.8909H3.81818C3.33597 13.8909 2.87351 13.6994 2.53253 13.3584C2.19156 13.0174 2 12.5549 2 12.0727C2 11.5905 2.19156 11.1281 2.53253 10.7871C2.87351 10.4461 3.33597 10.2545 3.81818 10.2545H3.9C4.2009 10.2475 4.49273 10.1501 4.73754 9.97501C4.98236 9.79991 5.16883 9.55521 5.27273 9.27273C5.39374 8.99853 5.42984 8.69437 5.37637 8.39947C5.3229 8.10456 5.18231 7.83244 4.97273 7.61818L4.91818 7.56364C4.74913 7.39478 4.61503 7.19425 4.52353 6.97353C4.43203 6.7528 4.38493 6.51621 4.38493 6.27727C4.38493 6.03834 4.43203 5.80174 4.52353 5.58102C4.61503 5.36029 4.74913 5.15977 4.91818 4.99091C5.08704 4.82186 5.28757 4.68775 5.50829 4.59626C5.72901 4.50476 5.96561 4.45766 6.20455 4.45766C6.44348 4.45766 6.68008 4.50476 6.9008 4.59626C7.12152 4.68775 7.32205 4.82186 7.49091 4.99091L7.54545 5.04545C7.75971 5.25503 8.03183 5.39562 8.32674 5.4491C8.62164 5.50257 8.9258 5.46647 9.2 5.34545H9.27273C9.54161 5.23022 9.77093 5.03887 9.93245 4.79497C10.094 4.55107 10.1807 4.26526 10.1818 3.97273V3.81818C10.1818 3.33597 10.3734 2.87351 10.7144 2.53253C11.0553 2.19156 11.5178 2 12 2C12.4822 2 12.9447 2.19156 13.2856 2.53253C13.6266 2.87351 13.8182 3.33597 13.8182 3.81818V3.9C13.8193 4.19253 13.906 4.47834 14.0676 4.72224C14.2291 4.96614 14.4584 5.15749 14.7273 5.27273C15.0015 5.39374 15.3056 5.42984 15.6005 5.37637C15.8954 5.3229 16.1676 5.18231 16.3818 4.97273L16.4364 4.91818C16.6052 4.74913 16.8057 4.61503 17.0265 4.52353C17.2472 4.43203 17.4838 4.38493 17.7227 4.38493C17.9617 4.38493 18.1983 4.43203 18.419 4.52353C18.6397 4.61503 18.8402 4.74913 19.0091 4.91818C19.1781 5.08704 19.3122 5.28757 19.4037 5.50829C19.4952 5.72901 19.5423 5.96561 19.5423 6.20455C19.5423 6.44348 19.4952 6.68008 19.4037 6.9008C19.3122 7.12152 19.1781 7.32205 19.0091 7.49091L18.9545 7.54545C18.745 7.75971 18.6044 8.03183 18.5509 8.32674C18.4974 8.62164 18.5335 8.9258 18.6545 9.2V9.27273C18.7698 9.54161 18.9611 9.77093 19.205 9.93245C19.4489 10.094 19.7347 10.1807 20.0273 10.1818H20.1818C20.664 10.1818 21.1265 10.3734 21.4675 10.7144C21.8084 11.0553 22 11.5178 22 12C22 12.4822 21.8084 12.9447 21.4675 13.2856C21.1265 13.6266 20.664 13.8182 20.1818 13.8182H20.1C19.8075 13.8193 19.5217 13.906 19.2778 14.0676C19.0339 14.2291 18.8425 14.4584 18.7273 14.7273Z"></path></svg>

                            <span>Settings</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                            <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                            <span className="control-button-with-key-right-part-connector">+</span>
                            <span className="control-button-with-key-right-part-text">,</span>
                        </div>
                    </button>
                </div> */}

                <div className="control">
                    <button
                        className={`control-button control-button-with-key`}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onClick={() => {
                            setTimeout(() => { dispatch(toggleBtnModalShortcuts()); }, 20);
                            setTimeout(() => { handleCancel(); }, 70);
                        }}
                    >
                        <div className='control-button-with-key-left-part'>
                            <svg className="icon-keyboard-menu" width="16px" height="21px" viewBox="0 0 24 24" fill="none"><g> <path d="M7 14H17M6 10H6.01M10 10H10.01M14 10H14.01M18 10H18.01M5.2 18H18.8C19.9201 18 20.4802 18 20.908 17.782C21.2843 17.5903 21.5903 17.2843 21.782 16.908C22 16.4802 22 15.9201 22 14.8V9.2C22 8.0799 22 7.51984 21.782 7.09202C21.5903 6.71569 21.2843 6.40973 20.908 6.21799C20.4802 6 19.9201 6 18.8 6H5.2C4.07989 6 3.51984 6 3.09202 6.21799C2.71569 6.40973 2.40973 6.71569 2.21799 7.09202C2 7.51984 2 8.07989 2 9.2V14.8C2 15.9201 2 16.4802 2.21799 16.908C2.40973 17.2843 2.71569 17.5903 3.09202 17.782C3.51984 18 4.0799 18 5.2 18Z" stroke="currentColor" strokeWidth="2px" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                            <span className='t_keyboard_shortcuts'>{t('dropdown_ctrl_settings.keyboard_shortcuts')}</span>
                        </div>
                        <div className='control-button-with-key-right-part'>
                            <span className="control-button-with-key-right-part-text">{shortKeysMap?.CTRL}</span>
                            <span className="control-button-with-key-right-part-connector">+</span>
                            <span className="control-button-with-key-right-part-text">/</span>
                        </div>
                    </button>
                </div>

                <div className="control">
                    <button
                        className={`control-button disabled`}
                        type="button"
                        title=""
                        aria-pressed="false"
                    // onClick={() => {
                    //     const url = `/onboarding`;
                    //     // setTimeout(() => {
                    //     // dispatch(toggleSidebarProfileMenu());
                    //     history.push(url);
                    //     // }, 100);
                    // }}
                    >
                        <svg className="icon-onboarding" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path strokeWidth="2" stroke="white" d="M11.5 5H11.9344C14.9816 5 16.5053 5 17.0836 5.54729C17.5836 6.02037 17.8051 6.71728 17.6702 7.39221C17.514 8.17302 16.2701 9.05285 13.7823 10.8125L9.71772 13.6875C7.2299 15.4471 5.98599 16.327 5.82984 17.1078C5.69486 17.7827 5.91642 18.4796 6.41636 18.9527C6.99474 19.5 8.51836 19.5 11.5656 19.5H12.5M8 5C8 6.65685 6.65685 8 5 8C3.34315 8 2 6.65685 2 5C2 3.34315 3.34315 2 5 2C6.65685 2 8 3.34315 8 5ZM22 19C22 20.6569 20.6569 22 19 22C17.3431 22 16 20.6569 16 19C16 17.3431 17.3431 16 19 16C20.6569 16 22 17.3431 22 19Z"></path></svg>
                        {t('m_profile_menu.onboarding')}
                    </button>
                </div>

                {/* <div className="control">
                    <button
                        className={`control-button`}
                        type="button"
                        title=""
                        aria-label="Color"
                        aria-pressed="false"
                        onClick={() => {
                            trackEvent('app_sidebar_profile_homepage', {
                                page: 'app',
                                type: 'sidebar',
                                subtype: 'profile',
                            });

                            dispatch(toggleSidebarProfileMenu());
                            setTimeout(() => { window.open('https://dbdesign.io', '_blank'); }, 100)
                        }}
                    >

                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{
                            marginRight: '1px'
                        }}><path d="M12.982 2.764c-.351-.273-.527-.41-.72-.462a1 1 0 0 0-.523 0c-.194.052-.37.189-.721.462L4.235 8.039c-.453.353-.68.53-.843.75a2 2 0 0 0-.318.65C3 9.704 3 9.991 3 10.565V17.8c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C4.52 21 5.08 21 6.2 21h2c.28 0 .42 0 .527-.055a.5.5 0 0 0 .218-.218C9 20.62 9 20.48 9 20.2v-6.6c0-.56 0-.84.109-1.054a1 1 0 0 1 .437-.437C9.76 12 10.04 12 10.6 12h2.8c.56 0 .84 0 1.054.109a1 1 0 0 1 .437.437C15 12.76 15 13.04 15 13.6v6.6c0 .28 0 .42.055.527a.5.5 0 0 0 .218.218c.107.055.247.055.527.055h2c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C21 19.48 21 18.92 21 17.8v-7.235c0-.574 0-.861-.074-1.126a2 2 0 0 0-.318-.65c-.163-.22-.39-.397-.843-.75z"></path></svg>
                        Homepage
                    </button>
                </div> */}

                <div className="horizontal-line"></div>

                <div className="control">
                    <button
                        className={`control-button`}
                        type="button"
                        title=""
                        aria-pressed="false"
                        onClick={() => {
                            trackEvent('app_sidebar_profile_sign_out', {
                                page: 'app',
                                type: 'sidebar',
                                subtype: 'profile',
                            });

                            dispatch(toggleSidebarProfileMenu());
                            dispatch(signOut())
                        }}

                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '2px', marginRight: '-1px' }}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" x2="9" y1="12" y2="12"></line></svg>
                        {t('b.log_out')}
                    </button>
                </div>

            </div>

            {isSubMenuOpen && (
                <div className={`language-sub-menu ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                    {/* <div className="language-sub-menu-label">Language</div> */}
                    <div className="language-sub-menu-list">
                        {LANGUAGES.map((lng) => {
                            const isActive = i18n.language === lng.code;
                            return (
                                <div className="control" key={lng.code}>
                                    <button
                                        className={`control-button ${isActive ? 'selected' : ''}`}
                                        type="button"
                                        onClick={() => {
                                            trackEvent('app_sidebar_profile_language_select', {
                                                page: 'app',
                                                type: 'sidebar',
                                                subtype: 'profile',
                                                language: lng.code,
                                            });
                                            i18n.changeLanguage(lng.code);
                                            dispatch(toggleSidebarProfileMenu());
                                            setIsSubMenuOpen(false);
                                        }}
                                    >
                                        <span className="language-name">{lng.native}</span>
                                        {isActive && <OkCheckIcon />}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModalProfileMenu;
